import { defineStore } from "pinia";

import type {
  IKundserviceAppApiClient,
  ReleaseNotes,
} from "@/composables/KundserviceAppApiClient";
import type { Notification } from "@/layout/app-header/notifications-menu";
import { formatDateToYYYYMMDD } from "@/common/formatters";

export interface Release {
  id: string;
  date: string;
  name: string;
  fullName: string;
  content?: string | null;
}

interface ReleaseState {
  isLoading: boolean;
  releases: Release[];
  currentRelease: Release | null;
}

let fetchReleasesPromise: Promise<Release[]> | null = null;

export const fullNamneToId = (fullName: string) => `release - ${fullName}`;

const releaseMapper = (release: ReleaseNotes): Release => {
  const fullName = `${release.name} ${formatDateToYYYYMMDD(release.date)}`;
  const id = fullNamneToId(fullName);
  const date = formatDateToYYYYMMDD(release.date);

  return {
    id,
    name: release.name,
    fullName,
    date,
  } as Release;
};

const notificationMapper = (release: Release): Notification => {
  return {
    id: release.id,
    type: "Release",
    title: release.name,
    date: release.date,
    link: `/releaser/${release.fullName}`,
    seen: false,
  } as Notification;
};

export const useReleaseStore = defineStore("release", {
  state: (): ReleaseState => ({
    isLoading: false,
    releases: [],
    currentRelease: null,
  }),
  actions: {
    async fetchReleases(apiClient: IKundserviceAppApiClient) {
      if (fetchReleasesPromise) return fetchReleasesPromise;

      this.isLoading = true;
      fetchReleasesPromise = apiClient
        .getReleaseNotesList()
        .then((releases) => releases.map(releaseMapper))
        .then((releases) => {
          this.releases = releases;
          return releases;
        })
        .catch((err) => {
          console.error(err);
          throw err;
        })
        .finally(() => {
          this.isLoading = false;
          fetchReleasesPromise = null;
        });

      return fetchReleasesPromise;
    },
    async fetchRelease(fullName: string, apiClient: IKundserviceAppApiClient) {
      try {
        this.isLoading = true;
        const releaseContent = await apiClient.getReleaseNotesContent(fullName);
        const release = this.getReleaseByFullName(fullName);
        if (release) {
          release.content = releaseContent.content;
          this.currentRelease = release;
        }
        return release;
      } catch (error) {
        console.error(error);
      } finally {
        this.isLoading = false;
      }
    },
  },
  getters: {
    getAsNotifications: (state: ReleaseState) => (): Notification[] => {
      const threeMonthsAgo = new Date();
      threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);
      const threeMonthsAgoFormatted = formatDateToYYYYMMDD(threeMonthsAgo);
      
      return state.releases
        .filter((release) => release.date >= threeMonthsAgoFormatted)
        .map(notificationMapper);
    },

    getReleaseYears: (state: ReleaseState) => () =>
      state.releases
        .map((release) => release.date.substring(0, 4))
        .filter((value, index, self) => self.indexOf(value) === index)
        .sort((a, b) => b.localeCompare(a)),

    getReleasesForYear: (state: ReleaseState) => (year: string) =>
      state.releases
        .filter((release) => release.date.startsWith(year))
        .sort((a, b) => b.date.localeCompare(a.date)),

    getLatestRelease: (state: ReleaseState) => () => state.releases[0],

    getReleaseByFullName: (state: ReleaseState) => (fullName: string) =>
      state.releases.find((release) => release.fullName === fullName),
  },
});
